// Small display-formatting helpers, local to this feature (S0 ownership: this goal
// touches only apps/web/src/routes/_authenticated/stores-transfers* and
// apps/web/src/features/stores-transfers/ — it does not add to a shared
// `lib/format.ts`, which doesn't exist yet). Mirrors services/api/src/modules/
// stores/shared.ts's `formatCents` (ZAR cents -> "R1234.56") so the web layer's
// ledger-effect preview reads the same way the API's own posting summary text does.

/** ZAR cents (may be fractional pre-rounding, e.g. an estimate) -> "R1,234.56". */
export function formatRands(cents: number): string {
  const negative = cents < 0;
  const rands = Math.abs(cents) / 100;
  const formatted = rands.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${negative ? '-' : ''}R${formatted}`;
}

/** Quantity display: trims trailing zeros beyond the base-UOM's 3dp but keeps at
 * least one decimal readable (e.g. "120" stays "120", "12.500" -> "12.5"). */
export function formatQty(qty: string): string {
  const n = Number(qty);
  if (!Number.isFinite(n)) return qty;
  return n.toLocaleString('en-ZA', { maximumFractionDigits: 3 });
}

/** `avgCostCentsPerUnit` (a decimal-string, 4dp) x `qty` (a decimal-string, 3dp) as a
 * display-only cents estimate. NOT the authoritative value — the real ledger effect
 * is only known once `recordMovement` runs at Post time (WAC can move between
 * preview and post if something else posts concurrently), which is why every screen
 * that uses this labels the number "Estimated". */
export function estimateValueCents(avgCostCentsPerUnit: string, qty: string): number {
  const avg = Number(avgCostCentsPerUnit);
  const q = Number(qty);
  if (!Number.isFinite(avg) || !Number.isFinite(q)) return 0;
  return avg * q;
}

/** Renders an ISO-ish date value (the API's `issuedAt`/`occurredAt` fields are typed
 * as `Date` via `z.coerce.date()` on the `@zebra/core` schemas, but travel over the
 * wire as JSON strings since `src/api/client.ts` does not re-parse responses through
 * zod — see its header comment. `new Date(x)` accepts either a `Date` or a string, so
 * this is safe regardless of which shape actually arrives at runtime.) */
export function formatDate(value: Date | string): string {
  return new Date(value).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function formatDateTime(value: Date | string): string {
  return new Date(value).toLocaleString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
